import React, { useState, useEffect } from 'react';
import { CheckCircleIcon, XCircleIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { PartnerTokenTierConfig } from '../../types/partnerToken';
import { partnerTokenService } from '../../services/partnerTokenService';
import { usePartnerTokenTiers } from '../../contexts/PartnerTokenTiersContext';

interface ManagePartnerTokensModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdated?: () => void;
}

export const ManagePartnerTokensModal: React.FC<ManagePartnerTokensModalProps> = ({
  isOpen,
  onClose,
  onUpdated,
}) => {
  const { refresh: refreshGlobalTiers } = usePartnerTokenTiers();
  const [tiers, setTiers] = useState<PartnerTokenTierConfig[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [savingTier, setSavingTier] = useState<string | null>(null);
  const [savingAll, setSavingAll] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Local editable state mapped by tier
  const [formState, setFormState] = useState<
    Record<
      string,
      {
        name: string;
        badge_letter: string;
        description: string;
        price_dollars: string;
        is_active: boolean;
      }
    >
  >({});

  const fetchTiers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await partnerTokenService.getAdminPartnerTokenTiers();
      setTiers(data);

      const initialForms: Record<string, any> = {};
      data.forEach((t) => {
        initialForms[t.tier] = {
          name: t.name || '',
          badge_letter: t.badge_letter || (t.name ? t.name[0].toUpperCase() : 'T'),
          description: t.description || '',
          price_dollars: t.price_cents ? (t.price_cents / 100).toString() : '0',
          is_active: t.is_active,
        };
      });
      setFormState(initialForms);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load partner token tiers.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchTiers();
      setSuccess(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleInputChange = (tier: string, field: string, value: any) => {
    setFormState((prev) => ({
      ...prev,
      [tier]: {
        ...prev[tier],
        [field]: value,
      },
    }));
  };

  const isTierDirty = (tierKey: string) => {
    const original = tiers.find((t) => t.tier === tierKey);
    const current = formState[tierKey];
    if (!original || !current) return false;
    const origPrice = original.price_cents ? (original.price_cents / 100).toString() : '0';
    return (
      (current.name || '').trim() !== (original.name || '').trim() ||
      (current.badge_letter || '').trim() !== (original.badge_letter || '').trim() ||
      (current.description || '').trim() !== (original.description || '').trim() ||
      (current.price_dollars || '0').trim() !== origPrice ||
      Boolean(current.is_active) !== Boolean(original.is_active)
    );
  };

  const hasAnyDirty = tiers.some((t) => isTierDirty(t.tier));

  const handleSaveTier = async (tier: string) => {
    const item = formState[tier];
    if (!item) return;

    try {
      setSavingTier(tier);
      setError(null);
      setSuccess(null);

      const priceCents = Math.round(parseFloat(item.price_dollars || '0') * 100);

      const updated = await partnerTokenService.updatePartnerTokenTier(tier, {
        name: item.name.trim(),
        badge_letter: item.badge_letter.trim(),
        description: item.description.trim(),
        price_cents: priceCents > 0 ? priceCents : undefined,
        is_active: item.is_active,
      });

      setTiers((prev) => prev.map((t) => (t.tier === tier ? updated : t)));
      setSuccess(`Token tier "${item.name}" updated successfully.`);
      await refreshGlobalTiers();
      if (onUpdated) onUpdated();
    } catch (err: any) {
      setError(err?.response?.data?.detail || `Failed to update token tier ${tier}.`);
    } finally {
      setSavingTier(null);
    }
  };

  const handleSaveAll = async (): Promise<boolean> => {
    const dirtyTiers = tiers.filter((t) => isTierDirty(t.tier));
    if (dirtyTiers.length === 0) return true;

    try {
      setSavingAll(true);
      setError(null);
      setSuccess(null);

      for (const t of dirtyTiers) {
        const item = formState[t.tier];
        if (!item) continue;
        const priceCents = Math.round(parseFloat(item.price_dollars || '0') * 100);
        await partnerTokenService.updatePartnerTokenTier(t.tier, {
          name: item.name.trim(),
          badge_letter: item.badge_letter.trim(),
          description: item.description.trim(),
          price_cents: priceCents > 0 ? priceCents : undefined,
          is_active: item.is_active,
        });
      }

      setSuccess('All partner token tier changes saved successfully.');
      await refreshGlobalTiers();
      await fetchTiers();
      if (onUpdated) onUpdated();
      return true;
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to save all token tiers.');
      return false;
    } finally {
      setSavingAll(false);
    }
  };

  const handleDone = async () => {
    if (hasAnyDirty) {
      const ok = await handleSaveAll();
      if (!ok) return;
    }
    onClose();
  };

  const tierColors: Record<string, { badge: string; border: string; bg: string }> = {
    simple: { badge: 'bg-blue-100 text-blue-800 border-blue-300', border: 'border-blue-200', bg: 'bg-blue-50/30' },
    gold: { badge: 'bg-amber-100 text-amber-900 border-amber-300', border: 'border-amber-200', bg: 'bg-amber-50/30' },
    premium: { badge: 'bg-purple-100 text-purple-900 border-purple-300', border: 'border-purple-200', bg: 'bg-purple-50/30' },
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden border border-slate-200 animate-in fade-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50">
          <div>
            <div className="flex items-center space-x-2">
              <span className="p-1.5 bg-indigo-100 text-indigo-900 rounded-lg">
                <SparklesIcon className="w-5 h-5" />
              </span>
              <h3 className="font-bold text-slate-900 text-lg">Insurance Partner Token Names & Tiers</h3>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Customize the names, abbreviations, and descriptions of tabletop tokens granted to insurance partners.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 text-2xl font-light leading-none p-1"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto space-y-6">
          {success && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg text-sm flex items-center space-x-2">
              <CheckCircleIcon className="w-5 h-5 text-emerald-600 flex-shrink-0" />
              <span>{success}</span>
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-800 rounded-lg text-sm flex items-center space-x-2">
              <XCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {loading ? (
            <div className="p-12 text-center text-slate-500 text-sm">Loading token tiers...</div>
          ) : (
            <div className="space-y-6">
              {tiers.map((t) => {
                const form = formState[t.tier] || {
                  name: t.name,
                  badge_letter: t.badge_letter || 'T',
                  description: t.description || '',
                  price_dollars: (t.price_cents ? t.price_cents / 100 : 0).toString(),
                  is_active: t.is_active,
                };
                const colorTheme = tierColors[t.tier] || {
                  badge: 'bg-slate-100 text-slate-800 border-slate-300',
                  border: 'border-slate-200',
                  bg: 'bg-slate-50',
                };

                return (
                  <div
                    key={t.tier}
                    className={`p-5 rounded-xl border ${colorTheme.border} ${colorTheme.bg} space-y-4 shadow-2xs relative`}
                  >
                    {/* Header Row & Preview */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <span
                          className={`w-9 h-9 rounded-lg border font-bold flex items-center justify-center text-sm shadow-xs ${colorTheme.badge}`}
                        >
                          {form.badge_letter || 'T'}
                        </span>
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className="font-bold text-slate-900 text-base">{form.name || t.tier}</span>
                            {isTierDirty(t.tier) && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-300">
                                Unsaved
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-slate-500 block font-mono">
                            system key: <span className="text-slate-700">{t.tier}</span>
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <span className="text-xs font-semibold text-slate-500">Preview:</span>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold border ${colorTheme.badge}`}
                        >
                          {form.badge_letter} · {form.name}
                        </span>
                      </div>
                    </div>

                    {/* Inputs */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                          Display Name *
                        </label>
                        <input
                          type="text"
                          required
                          value={form.name}
                          onChange={(e) => handleInputChange(t.tier, 'name', e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleSaveTier(t.tier);
                            }
                          }}
                          placeholder="e.g. Prepared, Cyber Core, Executive"
                          className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                          Badge Letter
                        </label>
                        <input
                          type="text"
                          maxLength={3}
                          value={form.badge_letter}
                          onChange={(e) => handleInputChange(t.tier, 'badge_letter', e.target.value.toUpperCase())}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleSaveTier(t.tier);
                            }
                          }}
                          placeholder="e.g. S, G, P, E"
                          className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-sm font-bold text-center bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                        Token Description (shown to insurance partners & clients)
                      </label>
                      <textarea
                        rows={2}
                        value={form.description}
                        onChange={(e) => handleInputChange(t.tier, 'description', e.target.value)}
                        placeholder="Describe what is included when this token is granted..."
                        className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-xs leading-relaxed bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>

                    {/* Pricing & Save Action */}
                    <div className="flex items-center justify-between pt-2 border-t border-slate-200/60">
                      <div className="flex items-center space-x-2">
                        <label className="text-xs font-semibold text-slate-600">Unit Price ($):</label>
                        <div className="relative w-28">
                          <span className="absolute left-2.5 top-1.5 text-xs text-slate-400">$</span>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={form.price_dollars}
                            onChange={(e) => handleInputChange(t.tier, 'price_dollars', e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleSaveTier(t.tier);
                              }
                            }}
                            className="w-full pl-6 pr-2 py-1 border border-slate-300 rounded-md text-xs font-semibold bg-white"
                          />
                        </div>
                      </div>

                      <button
                        type="button"
                        disabled={savingTier === t.tier || !form.name.trim()}
                        onClick={() => handleSaveTier(t.tier)}
                        className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-colors shadow-2xs ${
                          isTierDirty(t.tier)
                            ? 'bg-amber-600 hover:bg-amber-700 text-white animate-pulse'
                            : 'bg-indigo-900 hover:bg-indigo-800 text-white'
                        } disabled:opacity-50`}
                      >
                        {savingTier === t.tier ? 'Saving...' : isTierDirty(t.tier) ? 'Save Tier *' : 'Save Tier'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-100 bg-slate-50 flex items-center justify-between text-xs text-slate-500">
          <span>Changes take effect immediately across all client and partner invite screens.</span>
          <div className="flex items-center space-x-2">
            {hasAnyDirty && (
              <button
                type="button"
                disabled={savingAll}
                onClick={handleSaveAll}
                className="px-4 py-1.5 bg-indigo-900 text-white rounded-lg text-xs font-semibold hover:bg-indigo-800 disabled:opacity-50 transition-colors shadow-2xs"
              >
                {savingAll ? 'Saving all...' : 'Save All Changes'}
              </button>
            )}
            <button
              type="button"
              disabled={savingAll}
              onClick={handleDone}
              className="px-4 py-1.5 border border-slate-300 rounded-lg text-xs font-medium text-slate-700 hover:bg-white transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManagePartnerTokensModal;
